import type React from "react";
import { Group, Text, Tooltip } from "@mantine/core";
import { Badge } from "@xplatform/design-system";
import type { MatchTier } from "@models/search";

/**
 * How the answer was reached — the most useful thing about a result set, and
 * the one a percentage cannot tell you.
 *
 * The service is a ladder of tiers: `golden` and `exact` are decisions made by
 * code (deterministic, same answer every run, no model involved), `rerank` is
 * a model's ordering of what code could not settle. A reviewer deciding
 * whether the catalogue holds a value wants `exact` with no hits — not
 * `rerank` with poor ones.
 */
const TIERS: Record<MatchTier, { label: string; color: string; help: string }> = {
  golden: {
    label: "Golden",
    color: "yellow",
    help: "A confirmed past mapping for this exact input was replayed. No model involved.",
  },
  exact: {
    label: "Exact",
    color: "teal",
    help: "A record's own value IS what you typed. Decided by code: reproducible, no model involved.",
  },
  ann_score: {
    label: "Score gate",
    color: "orange",
    help: "A similarity threshold fired. Off by default; an operator enabled it.",
  },
  rerank: {
    label: "Reranked",
    color: "violet",
    help: "Code could not decide, so the model ordered the candidates. Read the scores.",
  },
  no_match: {
    label: "No match",
    color: "gray",
    help: "Nothing matched, or the query was placeholder text.",
  },
};

interface MatchTierBadgeProps {
  tier?: MatchTier;
  /** Stages that silently fell back for this query. */
  degraded?: string[];
}

const MatchTierBadge: React.FC<MatchTierBadgeProps> = ({ tier, degraded }) => {
  if (!tier) return null;
  const meta = TIERS[tier];
  if (!meta) return null;

  return (
    <Group gap="xs" wrap="nowrap">
      <Tooltip label={meta.help} multiline w={280} withArrow>
        <Badge variant="light" color={meta.color} size="sm">
          {meta.label}
        </Badge>
      </Tooltip>
      {degraded && degraded.length > 0 && (
        <Tooltip
          label={`These stages fell back: ${degraded.join(", ")}. Treat a missing result as unknown, not as absent.`}
          multiline
          w={280}
          withArrow
        >
          <Badge variant="light" color="red" size="sm">
            Degraded
          </Badge>
        </Tooltip>
      )}
      <Text size="xs" c="dimmed">
        {meta.help}
      </Text>
    </Group>
  );
};

export default MatchTierBadge;
